import React from "react";
import { Palette } from "lucide-react";

/** Accepts the hex the browser's color input needs, else a grey, so a half-typed code never trips the picker. */
export const pickerValue = (value: string): string => (/^#[0-9A-F]{6}$/i.test(value) ? value : "#888888");

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

/**
 * The brand color field: a hex code with a swatch that opens the color picker. The same field in every place a stream's color
 * is set (first-stream settings, adding a stream), so they cannot drift apart.
 */
const ColorField: React.FC<Props> = ({ id, label, value, onChange, placeholder, readOnly }) => (
  <div className="group">
    <label htmlFor={id} className="block text-sm font-bold text-slate-500 mb-2 ml-1">{label}</label>
    <div className="relative">
      <Palette className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} aria-hidden="true" />
      <input
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className={`w-full bg-slate-50 border rounded-2xl py-3 pl-12 pr-16 outline-none transition-all ${readOnly
          ? "border-transparent cursor-not-allowed text-slate-600"
          : "border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"}`}
      />
      <div className="h-8 w-8 rounded-xl absolute top-1/2 right-3 -translate-y-1/2 shadow-sm border-2 border-white overflow-hidden" style={{ backgroundColor: value || "#888888" }}>
        {!readOnly && (
          <input
            type="color"
            aria-label={`${label}: pick a color`}
            value={pickerValue(value)}
            onChange={(e) => onChange(e.target.value)}
            className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-0 cursor-pointer p-0 m-0"
          />
        )}
      </div>
    </div>
  </div>
);

export default ColorField;
